import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, Settings, User, Search, Menu, Filter, Shield, FlaskConical, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getIconStyle, getTrendStyle } from '../../config/statusColors';

// Styled brand name — "Forwards" black, "flow" gray
export const BrandName = ({ variant = 'default', className = '' }) => (
  <span className={className}>
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

export const brandText = (text, variant = 'default') => {
  if (typeof text !== 'string' || !text.includes('Forwardsflow')) return text;
  return text.split('Forwardsflow').flatMap((part, i, arr) =>
    i < arr.length - 1 ? [part, <BrandName key={i} variant={variant} />] : [part]
  );
};

// Forwardsflow Logo
export const Logo = ({ size = 'default', showText = true, variant = 'default' }) => {
  const sizeClasses = { small: 'w-7 h-7', default: 'w-9 h-9', large: 'w-11 h-11' };
  const textSizeClasses = { small: 'text-base', default: 'text-xl', large: 'text-2xl' };

  return (
    <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
      <img
        src="/logo.svg"
        alt="Forwardsflow"
        className={`${sizeClasses[size]} object-contain`}
      />
      {showText && (
        <span className={`${textSizeClasses[size]} font-sans font-normal tracking-tight`}>
          <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
          <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
        </span>
      )}
    </Link>
  );
};

// Environment Badge Component
export const EnvironmentBadge = ({ compact = false }) => {
  const { user } = useAuth();
  const environment = user?.environment || 'demo';
  const isProduction = environment === 'production';
  
  if (compact) {
    return (
      <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-none font-mono text-[10px] uppercase tracking-wider font-medium border border-zinc-300 bg-zinc-50 text-zinc-700">
        {isProduction ? (
          <Shield className="w-3 h-3 text-black" />
        ) : (
          <FlaskConical className="w-3 h-3 text-black" />
        )}
        {isProduction ? 'LIVE' : 'DEMO'}
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 px-3 py-1 border border-zinc-300 bg-zinc-50 rounded-none font-mono text-xs uppercase tracking-wider text-black font-medium">
      {isProduction ? (
        <>
          <Shield className="w-3.5 h-3.5" />
          <span>Production</span>
        </>
      ) : (
        <>
          <FlaskConical className="w-3.5 h-3.5" />
          <span>Demo</span>
        </>
      )}
    </div>
  );
};

// Environment Banner
export const EnvironmentBanner = () => {
  const { user } = useAuth();
  const environment = user?.environment || 'demo';
  const isProduction = environment === 'production';
  
  if (isProduction) return null;
  
  return (
    <div className="bg-[#4D4D4D] text-white text-center py-1.5 px-4 font-mono text-xs uppercase tracking-[0.15em] font-medium flex items-center justify-center gap-2 border-b border-[#4D4D4D]">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
      <span>DEMO MODE — All data simulated for demonstration purposes</span>
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
    </div>
  );
};

// Header Component
export const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  return (
    <header className="h-16 bg-white border-b border-zinc-200 px-6 flex items-center justify-between sticky top-0 z-30 font-sans">
      <div className="flex items-center gap-4">
        {onMenuClick && (
          <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-zinc-100 rounded-none border border-zinc-300">
            <Menu className="w-5 h-5 text-black" />
          </button>
        )}
        <div className="hidden lg:flex items-center gap-3"><Logo size="small" /></div>
        <EnvironmentBadge compact />
      </div>

      <div className="hidden md:flex flex-1 max-w-md mx-8">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input 
            type="text" 
            placeholder="Search..." 
            className="w-full pl-10 pr-4 py-2 bg-transparent border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 focus:outline-none focus:border-[#4D4D4D] transition-colors" 
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button className="relative p-2 hover:bg-zinc-100 rounded-none border border-zinc-200">
          <Bell className="w-4 h-4 text-zinc-700" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-[#4D4D4D] rounded-none"></span>
        </button>

        <button className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-none hover:border-[#4D4D4D] transition-colors font-mono text-xs uppercase tracking-wider text-black">
          <Filter className="w-3.5 h-3.5 text-zinc-600" />
          <span>Filters</span>
        </button>

        <div className="relative">
          <button 
            onClick={() => setShowUserMenu(!showUserMenu)} 
            className="flex items-center gap-3 pl-3 pr-2 py-1.5 border border-zinc-300 rounded-none hover:border-[#4D4D4D] transition-colors bg-white"
          >
            <div className="w-7 h-7 bg-[#4D4D4D] text-white rounded-none flex items-center justify-center font-mono text-xs font-bold">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-sans font-bold text-black">{user?.firstName || user?.name || 'User'}</p>
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-none border border-zinc-300 p-2 shadow-none z-50">
              <div className="px-3 py-2 border-b border-zinc-200 mb-1">
                <p className="text-xs font-bold text-black font-sans">{user?.firstName} {user?.lastName}</p>
                <p className="text-[10px] font-mono text-zinc-500">{user?.email}</p>
                <div className="mt-2">
                  <EnvironmentBadge />
                </div>
              </div>
              <button className="w-full px-3 py-2 text-left font-mono text-xs uppercase tracking-wider text-zinc-700 hover:bg-zinc-100 hover:text-black flex items-center gap-2 transition-colors">
                <Settings className="w-3.5 h-3.5" /> Settings
              </button>
              <button onClick={logout} className="w-full px-3 py-2 text-left font-mono text-xs uppercase tracking-wider text-zinc-700 hover:bg-zinc-100 hover:text-black flex items-center gap-2 transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// Stat Card
export const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'blue' }) => {
  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-none transition-all hover:border-[#4D4D4D]">
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center">
          <Icon className="w-4 h-4 text-black" />
        </div>
        {trend && (
          <span className="font-mono text-xs px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-black rounded-none">
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">{label}</p>
        <p className="font-sans text-2xl font-bold text-black mt-1">{value}</p>
        {subValue && <p className="font-mono text-[10px] text-zinc-500 mt-1">{subValue}</p>}
      </div>
    </div>
  );
};

// Badge
export const Badge = ({ children, variant = 'default', status }) => {
  return (
    <span className="badge rounded-none font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-300">
      {children}
    </span>
  );
};

// Loading Spinner
export const LoadingSpinner = ({ size = 'default' }) => {
  const sizeClasses = { small: 'w-4 h-4', default: 'w-8 h-8', large: 'w-12 h-12' };
  return <div className={`w-8 h-8 border-2 border-[#4D4D4D] border-t-transparent rounded-full animate-spin ${sizeClasses[size]}`}></div>;
};

// Page Loading
export const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-white font-sans">
    <div className="flex flex-col items-center gap-4">
      <Logo size="large" />
      <LoadingSpinner size="large" />
      <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Loading platform...</p>
    </div>
  </div>
);

// Modal Component
export const Modal = ({ isOpen, onClose, title, children, size = 'default' }) => {
  if (!isOpen) return null;
  const sizeClasses = { small: 'max-w-md', default: 'max-w-lg', large: 'max-w-2xl' };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 font-sans" onClick={onClose}>
      <div className={`bg-white border border-zinc-300 rounded-none w-full ${sizeClasses[size]}`} onClick={e => e.stopPropagation()}>
        {title && (
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
            <h2 className="font-sans text-lg font-bold text-black">{title}</h2>
            <button onClick={onClose} className="p-1 hover:bg-zinc-200 border border-zinc-300 rounded-none">
              <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// Production Confirmation Dialog
export const ProductionConfirmDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirm Production Action",
  message = "This action will be executed in the production environment with real data.",
  actionLabel = "Confirm"
}) => {
  const { user } = useAuth();
  
  if (user?.environment !== 'production') {
    if (isOpen) {
      onConfirm();
      onClose();
    }
    return null;
  }
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 font-sans">
      <div className="bg-white border border-zinc-300 rounded-none p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="font-sans text-lg font-bold text-black">{title}</h3>
            <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-black" /> Production Environment
            </p>
          </div>
        </div>
        
        <p className="text-xs text-zinc-700 leading-relaxed mb-6 font-sans">{message}</p>
        
        <div className="bg-zinc-50 border border-zinc-300 p-3 mb-6 rounded-none">
          <p className="text-xs font-mono text-black">
            ⚠️ This action cannot be undone and will affect live financial operations.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-transparent border border-zinc-300 text-black font-mono text-xs uppercase tracking-wider hover:border-[#4D4D4D] transition-colors rounded-none"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="flex-1 py-3 px-4 bg-[#4D4D4D] border border-[#4D4D4D] text-white font-mono text-xs uppercase tracking-wider hover:bg-black transition-colors rounded-none"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Data Table
export const DataTable = ({ columns, data, emptyMessage = 'No data available' }) => {
  if (!data || data.length === 0) {
    return <div className="text-center py-8 font-mono text-xs text-zinc-500 uppercase tracking-wider">{emptyMessage}</div>;
  }

  return (
    <div className="overflow-x-auto border border-zinc-300 rounded-none">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#4D4D4D] bg-zinc-50">
            {columns.map((col, idx) => <th key={idx} className="table-header">{col.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-b border-zinc-200 hover:bg-zinc-50/80 transition-colors">
              {columns.map((col, colIdx) => (
                <td key={colIdx} className="table-cell">
                  {col.render ? col.render(row) : row[col.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default { Logo, Header, StatCard, Badge, LoadingSpinner, PageLoading, Modal, DataTable, EnvironmentBadge, EnvironmentBanner, ProductionConfirmDialog };
